// /home/oh33090489/real-estate-app/frontend/src/components/PropertyList.js

import React, { useState, useRef, useEffect } from "react";
import PhotoGallery from "./PhotoGallery";
//컴포넌트 components
import ProfitPrintDialog from "./ProfitPrintDialog";
import { formatEstateLine } from "../utils/estateFormatter";
import HoList from "./HoList";
import { updateContractYn } from "../api/propertyApi"; // 상단에 import 확인
// =========== 제미나이 제안 (추가) 2026.01.18
// 1. 아래 import 문을 반드시 추가해야 합니다.
import { getAddressKey } from "../utils/addressGroup";
// =========== 제미나이 제안 (추가) 2026.01.18

// y/N 판별 헬퍼
const yn = (v) => String(v ?? "").toLowerCase() === "y";

// 전화번호 포맷(국내 기준)
function fmtPhone(raw) {
  const d = String(raw || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("02")) {
    if (d.length === 9)  return `02-${d.slice(2,5)}-${d.slice(5)}`;      // 02-xxx-xxxx
    if (d.length === 10) return `02-${d.slice(2,6)}-${d.slice(6)}`;      // 02-xxxx-xxxx
  }
  if (d.length === 11)    return `${d.slice(0,3)}-${d.slice(3,7)}-${d.slice(7)}`; // 010-xxxx-xxxx
  if (d.length === 10)    return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`; // 0xx-xxx-xxxx
  if (d.length === 8)     return `${d.slice(0,4)}-${d.slice(4)}`;               // xxxx-xxxx
  return raw; // 포맷 불가시 원문 유지
}

// 층분할 여부 표시: part/whole → "n층 일부/전체"
function fmtFloorType1(v, floor) {
  if (!v) return null;
  const base =
    floor !== undefined &&
    floor !== null &&
    String(floor).trim() !== "" &&
    String(floor) !== "0"
      ? `${floor}층 `
      : "";
  if (v === "part") return `${base}일부`;
  if (v === "whole") return `${base}전체`;
  return null;
}

function telHref(raw){
  const d = String(raw || "").replace(/\D/g, "");
  return d ? `tel:${d}` : "";
}

// 숫자 파싱: 콤마 등 제거
const num = (v) => {
  const s = String(v ?? "").replace(/[^\d.-]/g, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

// 천단위 콤마
const fmt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString("ko-KR") : "";
};

// bld_type → 한글명 매핑
const BLD_TYPE_OPTIONS = [
  ["apt","아파트"],
  ["business-bld","업무용빌딩"],
  ["shop-house","상가주택"],
  ["land","토지"],
  ["sale-shop","분양상가"],
  ["s-home-bld","단독주택"],
  ["multi-household","다가구주택"],
  ["single-room-bld","원룸건물"],
  ["single-room","원룸"],
  ["shop","상가"],
  ["multi-purpose","다용도"],
  ["office","사무실"],
  ["e-apt","오피스텔"],
  ["etc","기타"],
  ["metro-house","도시형주택"],
  ["multi-house","다세대주택"],
  ["row-house","연립주택"],
  ["single-room-owner","원룸주인세대"],
  ["villa","빌라"],
];

const BLD_TYPE_LABEL_MAP = Object.fromEntries(BLD_TYPE_OPTIONS);

// estate_type → 한글명 매핑 (최종 fallback용)
const ESTATE_TYPE_LABEL_MAP = {
  residence: "주거용",
  commerce: "상업용",
  business: "업무용",
  profit: "수익형",
};


// ▼ 리스트 타이틀: 건물명/상호명 + 층/호/대체표기
function Title({ p }) {

  const withSuffix = (val, suffix) => {
    if (val === null || val === undefined) return null;
    const s = String(val).trim();
    if (!s) return null;
    return s.endsWith(suffix) ? s : `${s}${suffix}`;
  };

  const estateType = String(p.estate_type || "").trim();

  // ✅ grade_condition → title 스타일
  const grade = String(p.grade_condition || "").trim();
  // ★ 제목의 '기본' 글자색을 보라색(#3f02af)으로 지정.
  //   아래 if/else if 에서 등급값이 맞으면 그 등급 색으로 덮어쓰고,
  //   등급이 없거나 해당 없음이면 이 보라색이 그대로 나옴.
  let titleStyle = { color: "#3f02af" };   // ← 이렇게 변경
  if (grade === "manage") {
    // 연두 배경 + 가독성 텍스트
    titleStyle = {
      backgroundColor: "#c8f5a8",
      color: "#1f3d08",
      padding: "2px 6px",
      borderRadius: 4,
      display: "inline-block"
    };
  } else if (grade === "prohibit") {
    // 빨강 배경 + 가독성 텍스트
    titleStyle = {
      backgroundColor: "#ff4d4f",
      color: "#ffffff",
      padding: "2px 6px",
      borderRadius: 4,
      display: "inline-block"
    };
  } else if (grade === "attentive") {
    // 주황색 텍스트
    titleStyle = { color: "#fa8c16" };
  } else if (grade === "recommend-price") {
    // 파란색 텍스트
    titleStyle = { color: "#1677ff" };
  } else if (grade === "recommend") {
    // 초록색 텍스트
    titleStyle = { color: "#389e0d" };
  } else if (grade === "nonrecommend") {
    // 연회색 텍스트
    titleStyle = { color: "#bfbfbf" };
  }

  // ── 상업용 / 업무용 ─────────────────────────
  if (estateType === "commerce" || estateType === "business") {
    // left: 건물명 + 상호명, 둘 다 없으면 bld_type 한글
    const estateName =
      p.estate_name &&
      String(p.estate_name).trim() !== "" &&
      String(p.estate_name).trim() !== "0"
        ? String(p.estate_name).trim()
        : "";

    const shopName =
      p.shop_name &&
      String(p.shop_name).trim() !== "" &&
      String(p.shop_name).trim() !== "0"
        ? String(p.shop_name).trim()
        : "";

    const bldLabel =
      BLD_TYPE_LABEL_MAP[p.bld_type] ||
      (estateType === "business" ? "업무용빌딩" : "상업용");

    let left = "";
    if (estateName && shopName) {
      left = `${estateName} ${shopName}`;
    } else if (estateName) {
      left = estateName;
    } else if (shopName) {
      left = shopName;
    } else {
      left = bldLabel;
    }

    // ✅ right: 해당호수 → 호수대체 → (해당층 + 층분할여부)
    let right = "";

    const hoVal = Number(p.estate_ho) > 0 ? Number(p.estate_ho) : null;
    const hasFloorEtc =
      p.floor_etc &&
      String(p.floor_etc).trim() !== "" &&
      String(p.floor_etc).trim() !== "0";

    if (hoVal) {
      // 1) 해당호수
      right = `${hoVal}호`;
    } else if (hasFloorEtc) {
      // 2) 호수대체
      right = String(p.floor_etc).trim();
    } else if (p.floor_type1) {
      // 3) 해당층 + 층분할여부 (층 일부 / 층 전체)
      right = fmtFloorType1(p.floor_type1, p.floor) || "";
    } else {
      right = "";
    }

    const titleText = right ? `${left} • ${right}` : left;
    return (
      <div /* [NOTCONTRACT_TITLE_V1] 배지형 등급이면 pl-title-badge + 원래 글자색(--tc) → Layout.css */
      className={`title${titleStyle.backgroundColor ? " pl-title-badge" : ""}`}
      style={{ ...titleStyle, "--tc": titleStyle.color }}>
        {titleText}
      </div>
    );
  }

  // ── 그 외(residence 등): 기존 규칙 유지 ───────────
  const name =
    (p.estate_name && String(p.estate_name).trim()) ||
    BLD_TYPE_LABEL_MAP[p.bld_type] ||
    p.bld_type ||
    ESTATE_TYPE_LABEL_MAP[p.estate_type] ||
    p.estate_type ||
    "매물";

  const dongho = [
    withSuffix(p.estate_dong, "동"),
    withSuffix(p.estate_ho, "호"),
  ]
    .filter(Boolean)
    .join(" ");

  const fallback =
    p.floor_etc ||
    fmtFloorType1(p.floor_type1, p.floor) ||
    p.floor_type2 ||
    (p.floor ? `${p.floor}층` : "");

  const right = dongho || fallback || "";
  return (
    <div /* [NOTCONTRACT_TITLE_V1] 배지형 등급이면 pl-title-badge + 원래 글자색(--tc) → Layout.css */
      className={`title${titleStyle.backgroundColor ? " pl-title-badge" : ""}`}
      style={{ ...titleStyle, "--tc": titleStyle.color }}>
      {right ? `${name} • ${right}` : name}
    </div>
  );
}

function UpdateInfo({ p }) {
  const dt =
    (typeof fmtDateTime === "function" && fmtDateTime(p.update_date, p.update_time)) ||
    (p.update_date ? String(p.update_date) : "") ||
    (p.modify_date ? String(p.modify_date) : "");
  if (!dt) return null;
  const updater =
    [p.update_name, p.update_position].filter(Boolean).join(" ");

  return (
    <div className="meta">
      {updater ? `${updater} · ` : ""}
      수정 {dt}
    </div>
  );
}

function IdInfo({ p }) {
  const id = p.estate_no ?? p.id ?? null;
  if (!id) return null;
  return <div className="meta">No. {id}</div>;
}

function Address({ p }) {
  const road =
    String(p.roadAddress || p.road_address || "").trim();

  const jibun =
    String(
      p.jibunAddress ||
      p.jibun ||
      (p.dong ? `${p.dong} ${p.jibun || ""}` : "")
    ).trim();

  if (!road && !jibun) return null;

  return (
    <div className="addr">
      {road && <div>{road}</div>}
      {jibun && jibun !== road && (
        <div
          style={{
            fontSize: 12,
            color: "#666",
            marginTop: 2,
          }}
        >
          {jibun}
        </div>
      )}
    </div>
  );
}

function Price({ p }) {
  const et = String(p.estate_type || "").trim();
  const prices = [];

  // 1. 매매, 경매 등 '보증금 형식이 아닌' 순수 가격 정보만 추출
  if (p.price) {
    const rawPrice = String(p.price);
    // 중복 방지: "전세/월세/단기/분양" 글자가 없는 순수 매매가 등만 먼저 푸시
    if (!rawPrice.includes("전세") && !rawPrice.includes("월세") && !rawPrice.includes("단기") && !rawPrice.includes("분양")) {
      prices.push(rawPrice);
    }
  }

  // 1-2. 분양가 (price_bunyang 컬럼 명시적 출력)
  if (yn(p.rent_type_5sale) && p.price_bunyang) {
    prices.push(`분양 ${fmt(p.price_bunyang)}`);
  }

  // 3. 경매 (명시적 추가)
  if (yn(p.rent_type_6auction)) 
    prices.push(`경매${p.price_sale ? ` ${fmt(p.price_sale)}` : ""}`);

  // 2. 전세 (새로 추가한 fee_jeonse 컬럼 사용)
  if (yn(p.rent_type_1year) && p.fee_jeonse) {
    prices.push(`전세 ${fmt(p.fee_jeonse)}`);
  }

  // 3. 월세 (fee_year와 fee_month 사용)
  if (yn(p.rent_type_2month)) {
    prices.push(`월세 ${fmt(p.fee_year)}/${fmt(p.fee_month)}`);
  }

  // 4. 단기 (fee_year와 fee_month 공유)
  if (yn(p.rent_type_3short)) {
    prices.push(`단기 ${fmt(p.fee_year)}/${fmt(p.fee_month)}`);
  }

  // 5. 상업/업무용 시설권리금 및 합계 로직
  if (et === "commerce" || et === "business") {
    const fy = num(p.fee_year);          // 월세 보증금
    const pp = num(p.price_premium);     // 시설·권리
    if (pp != null && pp > 0) {
      const total = (fy ?? 0) + pp;      // 합계금액 = 보증금 + 권리금
      prices.push(`시설·권리 ${fmt(pp)} (합계 ${fmt(total)})`);
    }
  }

  return (
    <div className="price" style={{ whiteSpace: "normal", wordBreak: "keep-all" }}>
      {prices.filter(Boolean).join(" · ")}
    </div>
  );
}

  function Section({ title, left, right }) {
    return (
      <div className="detail-section">
        <div className="detail-grid">
          <div className="d-left">{left}</div>
          <div className="d-right">
            <div className="detail-title">{title}</div>
            {right}
          </div>
        </div>
      </div>
    );
  }

function RightCol({ p }) {
  
  const toNum = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const isY = yn;  // 한 줄만 유지

  // profit 전용 금액 표시 계산
  const estateType = String(p.estate_type || "").trim();
  // ProfitPrintDialog.js의 총보증금/총월세/총관리비와 동일한 우선순위
  const sumFromItems = (items = []) => {
    let deposit = 0, monthly = 0, mgmt = 0;
    for (const r of items) {
      const tn = v => Number(String(v ?? "").replace(/[, ]/g, "")) || 0;
      deposit += tn(r.deposit);
      monthly += tn(r.monthly);
      mgmt    += tn(r.mgmtFee);
    }
    return { deposit, monthly, mgmt };
  };
  const tn = v => Number(String(v ?? "").replace(/[, ]/g, "")) || 0;
  const itemsSum = sumFromItems(p.rent_items);

  // 총보증금: (임대현황 합계) -> sale_type_rent_year -> fee_year
  const depositY =
    (itemsSum.deposit > 0 ? itemsSum.deposit :
    (tn(p.sale_type_rent_year) || tn(p.fee_year)));

  // 총월세: (임대현황 합계) -> sale_type_rent_month -> fee_month
  const rentM =
    (itemsSum.monthly > 0 ? itemsSum.monthly :
    (tn(p.sale_type_rent_month) || tn(p.fee_month)));

  // 총관리비: (임대현황 합계) -> f_price_total_mgr -> (price_per_household * rent_household_cnt)
  const mgrM =
    (itemsSum.mgmt > 0 ? itemsSum.mgmt :
    (tn(p.f_price_total_mgr) || (tn(p.price_per_household) * tn(p.rent_household_cnt))));
  const showProfitMoney = estateType === "profit";  

  // ---- 임대승계/입주가능 포맷 ----
  const fmtSubLiving = (p) => (isY(p.sale_type_sub_living) ? "입주가능" : null);
  const fmtSubRent = (p) => {
    if (!isY(p.sale_type_sub_rent)) return null;
    const t = String(p.sale_type_rent || "").toLowerCase(); // 'y' | 'm' | 's'
    const y = p.sale_type_rent_year ?? "";
    const m = p.sale_type_rent_month ?? "";
    if (t === "y") return `임대승계(전세 ${y})`;
    if (t === "m") return `임대승계(월세 ${y}${m ? ` / ${m}` : ""})`;
    if (t === "s") return `임대승계(단기 ${y}${m ? ` / ${m}` : ""})`;
    return "임대승계";
  };

  // ---- 표시 조건: profit 이거나, residence 이면서 rent_type_4deal = 'y' ----
  const canShowDealSubs =
    p.estate_type === "profit" ||
    (p.estate_type === "residence" && isY(p.rent_type_4deal));

  // ---- 설비/상태 태그 ----
  const tags = [];
  if (isY(p.living_condition_ev)) tags.push("승강기");

  if (canShowDealSubs) {
    const living = fmtSubLiving(p);
    const rent   = fmtSubRent(p);
    if (living) tags.push(living);
    if (rent)   tags.push(rent);
  }

  if (isY(p.internal_condition_interior))  tags.push("인테리어");
  if (isY(p.internal_condition_water))     tags.push("수도시설");
  if (isY(p.internal_condition_city_gas))  tags.push("도시가스");
  if (isY(p.bath_division))                tags.push("화장실 남녀구분");
  if (isY(p.bath_inside))                  tags.push("화장실 내부");
  if (isY(p.external_condition_exsign))    tags.push("옥외광고");

  // 난방/냉방
  const heat = String(p.heat_condition || "");
  if      (heat === "center")       tags.push("중앙난방");
  else if (heat === "floor")        tags.push("층별난방");
  else if (heat === "private-heat") tags.push("개별난방");

  const cold = String(p.cold_condition || "");
  if      (cold === "center")  tags.push("중앙냉방");
  else if (cold === "private") tags.push("개별냉방");
  
  // ---- 기존 우측 정보 유지 ----
  const room = toNum(p.room_cnt);
  const bath = toNum(p.bath_cnt);
  const pph  = toNum(p.price_per_household); // 관리비(만원)
  const pcnt = toNum(p.parking_cnt);         // 주차대수
  const pfee = toNum(p.parking_fee);         // 주차비(만원)

  const ownerY = String(p.owner_household || "").toLowerCase() === "y";
  const oroom = toNum(p.owner_room);
  const obath = toNum(p.owner_bath);

  const usedM2 = toNum(p.used_scale);
  const usedPy = toNum(p.used_scale_square);
  const exM2   = toNum(p.exclusive_scale); // 전용 ㎡
  const exPy   = toNum(p.exclusive_scale_square ?? p.exclusive_area_p); // 전용 평 폴백

  const showUsedLine =
    (p.estate_type === "business" || p.estate_type === "commerce") &&
    (usedM2 !== null || usedPy !== null);

  const eligibleBldForExclusive = [
    "apt","e-apt","metro-house","multi-house","villa","row-house","sale-shop"
  ].includes(p.bld_type);


  
  return (
    <div className="right-col" style={{ minWidth: 260, textAlign: "left", display: "flex", flexDirection: "column", justifyContent: "flex-start", marginLeft: 0 }}>
      {showProfitMoney && depositY !== null && (
        <div>총임대보증금 {fmt(depositY)}</div>
      )}
      {showProfitMoney && (rentM !== null || mgrM !== null) && (
        <div>
          총월세 {fmt((rentM ?? 0) + (mgrM ?? 0))}
          {mgrM !== null ? ` (관리비 ${fmt(mgrM)} 포함)` : ""}
        </div>
      )}
      {/* 새 태그 라인 */}
      {tags.length > 0 && <div>{tags.join(" · ")}</div>}
      
      {/* 룸/욕실: residence만 */}
      {p.estate_type === "residence" && room !== null && bath !== null && (
        <div>{room} 룸 / {bath}</div>
      )}

      {/* 주인세대: profit */}
      {p.estate_type === "profit" && ownerY && oroom !== null && obath !== null && (
        <div>주인세대 {Math.floor(oroom)} 룸 / {Math.floor(obath)}</div>
      )}

      {/* 면적: business|commerce → 실면적 우선, 없으면 주거형 전용 */}
      {showUsedLine ? (
        <div>실 {usedM2 ?? "-"}㎡ ({usedPy ?? "-"} 평)</div>
      ) : eligibleBldForExclusive && exM2 !== null ? (
        <div>전용 {exM2}㎡ ({exPy ?? "-"} 평)</div>
      ) : null}

      {/* 베란다 */}
      {String(p.living_condition_terrace || "").toLowerCase() === "y" && <div>베란다</div>}

      {/* 관리비 */}
      {estateType !== "profit" && pph !== null && pph > 0 && <div>관 {pph}</div>}

      {/* 주차 */}
      {pcnt !== null && pcnt > 0 ? (
        <div>주차 {pcnt}대{pfee !== null && pfee >= 0.1 ? ` / ${pfee} 만원` : ""}</div>
      ) : (
        <div>주차 확인필요</div>
      )}

    </div>
  );
}



const FLOOR_MAP = { part:"층 일부", whole:"층 전체", bottom:"바닥층", parking:"주차장방", top:"옥탑방" };

// 값 중 첫 번째로 유효한 것 반환
const pick = (...vals) => {
  for (const v of vals) {
    if (v === null || v === undefined) continue;
    const s = String(v).trim();
    if (s !== "" && s.toLowerCase() !== "null" && s.toLowerCase() !== "undefined") {
      return s;
    }
  }
  return "";
};

function formatResidenceSubLine(p) {
  if (p.estate_type !== "residence") return "";

   const dong = pick(p.estate_dong);
   const name = pick(p.estate_name);
   const namePart = name ? `${name} •` : "";   // ← estate_name 뒤에 •
  const ho   = pick(p.estate_ho);


  const dongPart = dong ? `${dong}동` : "";
  let tail = "";

  if (ho) {
    tail = `${ho}호`;
  } else {
    const t = pick(p.floor_etc, p.floor_type1, p.floor_type2);
    if (t) tail = FLOOR_MAP[String(t).trim()] || String(t).trim();
  }

  // 건물명(•) (동/호/층)
  return [namePart, dongPart, tail].filter(Boolean).join(" ");
}

// ★ 1. 상업용/업무용 매물 이름과 호/층을 포맷하는 새 함수 추가
function formatCommercialLine(p = {}) {
  // 이름 부분: shop_name -> estate_name -> estate_type 순서로 대체
  const baseName = pick(p.shop_name, p.estate_name, p.estate_type);
  const namePart = baseName ? `${baseName} •` : "";   // ← 뒤에
  

  // ★ estate_dong 값을 가져와 "ㅇㅇ동" 형태로 만드는 로직 추가
  const dong = pick(p.estate_dong);
  const dongPart = dong ? `${dong}동` : "";

  // 층/호수 부분: estate_ho -> floor_etc -> floor_type1 -> floor 순서로 대체
  let floorPart = pick(p.estate_ho); // 1순위: 호
  const ho   = pick(p.estate_ho);
  
  if (ho) { // ★ 호 정보가 있으면
    floorPart = `${ho}호`; // ★ " 호"를 붙여줍니다.
  } else { // 호 정보가 없을 경우
    let floorToken = pick(p.floor_etc, p.floor_type1);
    if (floorToken) {
      floorPart = FLOOR_MAP[floorToken] || floorToken;
    } else if (pick(p.floor)) {
      floorPart = `${pick(p.floor)}층`;
    }
  }

  // 최종적으로 이름과 층/호수 정보를 합쳐서 반환
  return [namePart, dongPart, floorPart].filter(Boolean).join(" ");
}

export default function PropertyList({
  properties = [],
  loading = false,
  highlightedId = null,     // ★ 추가: 현재 선택된 id
  onSelect = () => {},      // ★ 추가: 클릭 시 선택 콜백
  addressGroups = {},
  onHover,
  onLeave,
  displaySource = "server",   // ← App에서 내려줄 값
  onEditEstate = () => {},
  onCopyEstate = () => {},
  onContractEstate = () => {},
  onRefresh, // ✅ 상위 App에서 전달한 onRefresh를 받습니다.
  checkedIds = [],          // [TOUR_CHECK_V1] 체크된 estate_no 배열
  onToggleCheck = null,     // [TOUR_CHECK_V1] null 이면 체크박스 자체가 안 보임
}) {
  
  const [printOpenFor, setPrintOpenFor] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [thumbVer, setThumbVer] = useState(Date.now());
  // App에서 내려준 properties 순서를 그대로 사용하기 위한 내부 사본
  const [list, setList] = useState(properties);

  // =========== 제미나이 제안 (삭제) 2026.01.18
  // const getAddressKey = (p) => p?.pnu;
  // =========== 제미나이 제안 (삭제) 2026.01.18

  useEffect(() => {
    console.log("🔴 PropertyList props[0]:", properties?.[0]?.estate_no);
  }, [properties]);


  // props가 바뀌면 항상 같은 순서로 반영
  useEffect(() => {
    setList(properties);
  }, [properties]);
  const withVer = (u) => u ? `${u}${u.includes('?') ? '&' : '?'}t=${thumbVer}` : u
  const FALLBACK_NOIMG = "/static/thumbs/no_img.jpg";
  const normalizeThumb = (u) => {
    if (!u || u === "null" || u === "undefined") return FALLBACK_NOIMG;
    let s = String(u).trim();

    // 구글 드라이브 썸네일 URL인 경우 파라미터 최적화
    if (s.includes("drive.google.com") && (s.includes("sz=") || s.includes("id="))) {
      try {
        // URL 파라미터 분리 및 sz 값 강제 조정
        const baseUrl = s.split('?')[0];
        const params = new URLSearchParams(s.split('?')[1]);
        
        if (params.has('id')) {
          params.set('sz', 'w200'); // 리스트용 크기로 고정
          return `${baseUrl}?${params.toString()}`;
        }
      } catch (e) {
        // URL 파싱 실패 시 원본에서 sz만 강제 교체 시도
        return s.includes("sz=") ? s.replace(/sz=[^&]+/, "sz=w200") : s;
      }
    }
    return s;
  };

  useEffect(() => {
    const handler = () => setThumbVer(Date.now());
    window.addEventListener("photo:changed", handler);
    return () => window.removeEventListener("photo:changed", handler);
  }, []);

  // 특정 매물의 커버 변경 이벤트 수신 → 리스트 한 항목만 즉시 갱신
  useEffect(() => {
    const onCover = (e) => {
      const { estateNo, url } = e.detail || {};
      if (!estateNo) return;
      setList(prev => (prev || []).map(x =>
        Number(x.estate_no) === Number(estateNo) ? { ...x, thumb_url: url || null } : x
      ));
      setThumbVer(Date.now()); // 캐시 버스터
    };
    window.addEventListener("photo:cover", onCover);
    return () => window.removeEventListener("photo:cover", onCover);
  }, []);

  const items = Array.isArray(list) ? list : [];     // ← 내부 list 사용
  //console.log('[PropertyList] items.len=', items.length, items[0]);
  if (items.length === 0) {
    return <div className="list empty">데이터 없음</div>;
  }
  // estate_no를 최우선. 숫자/문자 혼용 대비해 Number 우선 변환.
  const getId = (p, idx) => {
    const raw = (p?.estate_no ?? p?.id ?? idx);
    return Number.isFinite(Number(raw)) ? Number(raw) : String(raw);
  };

  // 거래유형 라벨러
  const tradeLabel = (p) => {
    const arr = Array.isArray(p.rent_types) ? p.rent_types : [];
    const m = {
      deal: "매매",     // rent_type_4deal === 'y'
      rent: "월세",
      sale: "분양",
      auction: "경매",
      short: "단기",
      charter: "전세",
    };
    if (arr.length) return m[arr[0]] || arr[0];
    // price 문자열에 "매매" 포함 시
    if (String(p.price||"").includes("매매")) return "매매";
    return null;
  };

  return (
    <>
      <div className="list property-list">
          {items.map((p, idx) => {
            const id = getId(p, idx);             // id는 Number 또는 String 하나로만
            const estateNo = p.estate_no;
            // 🔥 동일 소재지 그룹 계산
            const addressKey = getAddressKey(p);
            const sameAddressItems =
              addressKey && addressGroups[addressKey]
                ? addressGroups[addressKey].items
                : [];
            const selected = highlightedId != null && (p.id === highlightedId || p.estate_no === highlightedId || id === highlightedId);
            const notContract = String(p.contract_yn || "").toLowerCase() !== "y";
            const isDeleted = String(p.use_yn || "").toLowerCase() === "n"; // ★ 삭제 상태 확인
            const opened = Number(expandedId) === Number(id);
            return (
              <div
                key={estateNo ?? id}
                className={`list-row ${selected ? "selected" : ""} ${notContract ? "not-contract" : ""} ${isDeleted ? "deleted-row" : ""} ${opened ? "opened" : ""}`}
                style={isDeleted ? { backgroundColor: "#f2f2f2", opacity: 0.85 } : {}} // ★ 삭제된 경우 회색 배경 적용
                onClick={(e) => {
                  if (e.target.closest('.pg-root')) return; // 갤러리 내부 클릭은 행 토글 금지
                  if (!estateNo) return; // 🔒 estate_no 없으면 편집/선택 불가
                  if (opened) { setExpandedId(null); onSelect && onSelect(null); }
                  else { setExpandedId(estateNo); onSelect && onSelect(estateNo); }
                  console.log("[row-click]", { estateNo });
                }}
                >

            <div className="summary" style={{display:"flex", alignItems:"stretch", gap:12, paddingBottom:8, marginBottom:8}}>
                  {/* [TOUR_CHECK_V1] 안내내역 저장용 체크박스. props 없으면 렌더 안 됨 */}
                  {onToggleCheck && (
                    <div style={{display:"flex",alignItems:"center",paddingRight:4}} onClick={(e)=>e.stopPropagation()}>
                      <input type="checkbox" checked={(checkedIds||[]).includes(estateNo)} onChange={()=>onToggleCheck(p)} style={{width:18,height:18,cursor:"pointer"}} />
                    </div>
                  )}
                  {/* 대표썸네일 */}
                  {(p.estate_type === "residence"
                    || p.estate_type === "business"
                    || p.estate_type === "commerce"
                    || p.estate_type === "profit") && (
                    <div 
                      className="thumb-col skeleton-box" // ★ 스켈레톤 클래스 추가
                      style={{
                        marginRight: 0, 
                        width: 72, 
                        height: 72, 
                        borderRadius: 6, 
                        overflow: 'hidden' 
                      }}
                    >
                      <img
                        key={`${estateNo}-${thumbVer}`}
                        src={withVer(normalizeThumb(p.thumb_url))}
                        loading="lazy"
                        decoding="async"
                        onError={(e)=>{ e.currentTarget.onerror=null; e.currentTarget.src = withVer(FALLBACK_NOIMG); }}
                        onLoad={(e) => {
                          // ★ 로드 완료 시 부모의 스켈레톤 효과 제거
                          e.currentTarget.parentElement.classList.remove('skeleton-box');
                        }}
                        alt=""
                        style={{ width: 72, height: 72, objectFit: "contain", background: "#111" }} /* PHOTO_RATIO_V1 */
                      />
                    </div>
                  )}

                  {/* 좌측: 등록번호 · 거래유형 · 소재지 + 가격 */}
                  <div
                    className="left-col"
                    style={{
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      {/* ★ 삭제 매물일 경우 표시 배지 */}
                      {isDeleted && (
                        <span style={{ 
                          display: "inline-block",
                          backgroundColor: "#ff4d4f", 
                          color: "#fff", 
                          fontSize: "10px", 
                          padding: "2px 6px", 
                          borderRadius: "4px", 
                          fontWeight: "bold",
                          marginRight: "6px",
                          verticalAlign: "middle"
                        }}>삭제데이터</span>
                      )}
                      {/* 건물명 + 동/호(또는 층대체 포함) */}
                      <Title p={p} />
                      <Address p={p} />
                    </div>
                    <div>
                      <Price p={p} />
                      {String(p.estate_type || "").trim() === "profit" ? (
                        <div>{(p.site_amount_square ?? p.site_area_p) ? `${p.site_amount_square ?? p.site_area_p} 평` : null}</div>
                      ) : null}
                      <IdInfo p={p} />
                      <UpdateInfo p={p} />
                    </div>
                  </div>
                  {/* 우측 컬럼: 2열에 바짝 붙이기 위해 marginLeft: "auto"를 제거하고 gap(12px)만 유지 */}
                  <div className="right-col" style={{ minWidth: 260, textAlign: "left", display: "flex", flexDirection: "column", justifyContent: "flex-start", marginLeft: 12 }}>
                    <div>
                      <RightCol p={p} />
                    </div>
                  </div>
                </div>
                
                {/* 상세영역: 3열 레이아웃으로 개편 */}
                <div className={`details ${opened ? "expand" : ""}`}>
                  {opened && (
                    <div className="detail-container" style={{ display: "flex", gap: 12, padding: "10px 0", alignItems: "flex-start" }}>
                      
                      {/* 1열: 버튼 및 호리스트 영역 */}
                      <div className="detail-col-1" style={{ width: 72, flexShrink: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                        {/* 상세보기 버튼 추가 (EstateEditor 오버레이 호출) */}
                        <button 
                          onClick={(e) => { e.stopPropagation(); onEditEstate(p, "view"); }} 
                          style={{ width: "100%", padding: "6px 0", fontSize: "11px", fontWeight: "bold", cursor: "pointer", backgroundColor: "#2b6cb0", color: "#fff", border: "none", borderRadius: "4px" }}
                        >상세보기</button>
                        
                        {/* 🔒 거래완료: premium/admin 만 (masked=true 면 제한 등급) */}
                        {!p.masked && (
                        <button 
                          onClick={async (e) => { 
                            e.stopPropagation(); 
                            if (window.confirm("이 매물을 '거래완료' 상태로 변경하시겠습니까?")) {
                              try {
                                // API 호출 (contract_yn -> 'n'으로 변경)
                                const res = await updateContractYn(p.estate_no, "n");
                                if (res.ok) {
                                  alert("거래완료 처리가 되었습니다.");
                                  // 리스트 즉시 갱신 (App.js의 fetch 함수 등)
                                  if (typeof onRefresh === "function") onRefresh();
                                } else {
                                  alert("처리에 실패했습니다: " + res.error);
                                }
                              } catch (err) {
                                console.error(err);
                                alert("오류 발생: " + err.message);
                              }
                            }
                          }} 
                          style={{ 
                            width: "100%", 
                            padding: "4px 0", 
                            fontSize: "11px", 
                            cursor: "pointer",
                            // ★ contract_yn이 'n'이면 회색(#a0aec0), 아니면 주황색(#ed8936) 출력
                            backgroundColor: p.contract_yn === "n" ? "#a0aec0" : "#ed8936",
                            color: "#fff",
                            border: "none",
                            borderRadius: "4px",
                            fontWeight: "bold"
                          }}
                          // ★ 이미 완료된 경우 클릭 방지 처리 (선택 사항)
                          disabled={p.contract_yn === "n"}
                        >
                          거래완료
                        </button>
                        )}
                        
                        {p.estate_type === "profit" && (
                          <button onClick={(e) => { e.stopPropagation(); setPrintOpenFor(p); }} style={{ width: "100%", padding: "4px 0", fontSize: "11px", fontWeight: "bold", cursor: "pointer" }}>상세내역</button>
                        )}
                        
                        {/* [정상화] 호리스트 출력 제외 로직: 본인 외에 유효한 다른 호수가 있을 때만 출력 */}
                        {(() => {
                          const otherItems = sameAddressItems.filter(it => 
                            Number(it.estate_no) !== Number(p.estate_no) && 
                            (String(it.estate_ho || "").trim() !== "" || String(it.floor_etc || "").trim() !== "")
                          );

                          if (otherItems.length > 0) {
                            return (
                              <div style={{ marginTop: 10, borderTop: "1px solid #f0f0f0", paddingTop: "8px" }}>
                                <div style={{ fontSize: "10px", color: "#999", marginBottom: "6px", textAlign: "center", fontWeight: "600" }}>다른 호수</div>
                                <HoList 
                                  items={sameAddressItems} 
                                  currentNo={p.estate_no} 
                                  onSelect={(it) => { 
                                    if (!it?.estate_no) return; 
                                    onSelect?.(it.estate_no); 
                                    setExpandedId(it.estate_no); 
                                  }} 
                                />
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>

                      {/* 2열 & 3열 메인 영역: 왼쪽 밀착 및 40px 간격 유지 */}
                      <div className="detail-main-content" style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 15 }}>
                        <div style={{ display: "flex", gap: 30, justifyContent: "flex-start", alignItems: "flex-start" }}>
                          
                          {/* 2열 (상단 left-col 라인) */}
                          <div className="detail-col-2" style={{ width: "auto", minWidth: 300, wordBreak: "break-all", whiteSpace: "normal" }}>
                            <CommonDetailLeftOnly p={p} />
                          </div>

                          {/* 3열 (상단 right-col 라인, 2열 옆에 40px 간격으로 밀착) */}
                          <div className="detail-col-3" style={{ width: 260, minWidth: 260, flexShrink: 0, textAlign: "left" }}>
                            <CommonDetailRightOnly p={p} />
                          </div>
                        </div>

                        {/* 하단 사진 갤러리 제거 (EstateEditor 내부로 이동됨) */}

                      </div>

                    </div>
                  )}
                </div>
                
              </div>
            );
          })}
      </div>
      {/* 모달은 리스트 밖에서 단일 렌더 */}
      <ProfitPrintDialog
        key={printOpenFor?.estate_no || printOpenFor?.id || 'none'}
        open={!!printOpenFor}
        property={printOpenFor}
        onClose={()=>setPrintOpenFor(null)}
        displaySource={displaySource}   // ← 여기 추가
        onUpdated={({ estate_no, thumb_url, thumb_file_id }) => {
          // 리스트 데이터 즉시 갱신
          setList(prev => (prev || []).map(p =>
            Number(p.estate_no) === Number(estate_no)
              ? { ...p, thumb_url, thumb_file_id }
              : p
          ));
          setThumbVer(Date.now()); // 캐시 무효화
        }}
      />
    </>
  );
 }

// 메모 등 텍스트 안의 http(s) 주소를 클릭 가능한 링크로 바꿉니다.
//   문자열을 URL 기준으로 잘라, URL 조각만 <a> 로 감싸고 나머지는 그대로 둡니다.
//   (dangerouslySetInnerHTML 을 쓰지 않아 XSS 걱정이 없습니다)
function linkify(text) {
  if (typeof text !== "string") return text;
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer"
           style={{ color: "#2563eb", wordBreak: "break-all" }}>{part}</a>
      : part
  );
}

function Field({ label, value }) {
    if (value === null || value === undefined || value === "" || value === false) return null;
    return (
      <div 
        className="frow" 
        style={{ 
          wordBreak: "break-all", 
          overflowWrap: "anywhere", 
          whiteSpace: "pre-wrap", // 공백 유지하며 줄바꿈
          display: "block",      // flex 대신 block으로 설정하여 너비 안에서 흐르게 함
          width: "100%"
        }}
      >
        {label && <span style={{ fontWeight: 600, marginRight: 4 }}>{label}</span>}
        {typeof value === "string" ? linkify(value) : value}
      </div>
    );
  }

// 여러 줄 의뢰인 표시: 이름 + tel:링크
function ContactsBlock({ title, lines }) {
  const parseJSON = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    try { return typeof data === 'string' ? JSON.parse(data) : []; } 
    catch (e) { return []; }
  };

  // 1. 데이터 파싱 및 유효성 검사 (0, "0", 빈값 제거)
  const rawData = parseJSON(lines);
  const validData = rawData.filter(c => {
    const phone = String(c.number || c.phone || "").trim();
    // 값이 없거나, "0"이거나, 글자수가 너무 적은 경우 제외
    // [CONTACT_VIEW_SYNC_V1] 상세보기와 같은 기준 — 이름('0' 제외) 또는 번호(숫자 2자리 이상)
    const nm = String(c.name || "").trim();
    return (nm !== "" && nm !== "0") || phone.replace(/\D/g, "").length >= 2;
  });

  // 📍 유효한 데이터가 없으면 라벨을 포함한 전체 영역을 그리지 않음 (return null)
  if (validData.length === 0) return null;

  const color = title === "소유자" ? "#2b6cb0" : "#38a169";
  const bg = title === "소유자" ? "#f0f7ff" : "#f0fff4";

  return (
    <div style={{
      marginTop: "6px",
      padding: "6px 8px",
      backgroundColor: bg,
      borderRadius: "4px",
      borderLeft: `3px solid ${color}`
    }}>
      <div style={{ fontSize: '11px', fontWeight: 'bold', color: color, marginBottom: '4px' }}>
        [{title}]
      </div>
      
      {validData.map((c, i) => {
        const phone = String(c.number || c.phone || "").trim();
        return (
          <div key={i} style={{ fontSize: "12.5px", marginBottom: "2px" }}>
            {c.name && <span style={{ fontWeight: 600, marginRight: "5px" }}>{c.name}</span>}
            {/* 연락처 클릭 시 부모(.list-row, 717번 줄)의 펼치기/접기 onClick으로
                클릭 이벤트가 전달되지 않도록 막아줍니다. 이게 없으면 전화 연결과
                행 토글이 동시에 일어나서 전화 앱 연결이 끊깁니다. */}
            {phone.replace(/\D/g, "").length >= 2 ? (   /* [CONTACT_VIEW_SYNC_V1] */
<a 
              href={telHref(phone)} 
              style={{ color: color, textDecoration: "none" }}
              onClick={(e) => e.stopPropagation()}
            >
              {fmtPhone(phone)}
            </a>
            ) : (
              <span style={{ color: "#a0aec0" }}>번호 없음</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

  function pad2(n){return String(n).padStart(2,"0");}
  function fmtDateTime(dateStr, timeStr){
    const s = [String(dateStr||"").trim(), String(timeStr||"").trim()].filter(Boolean).join(" ");
    if (!s) return null;
    const tryISO = s.replace(" ", "T");
    const d = new Date(tryISO);
    if (!isNaN(d)) {
      return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
    }
    const m = s.match(/(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}):(\d{2}))/);
    return m ? `${m[1]} ${m[2]}:${m[3]}` : s;
  }

// [Left] ──── <등록일/준공일> / <건물규모> / <역거리> / <의뢰인연락처>
function CommonDetailLeftOnly({ p }) {
  // 1. 숫자 변환 헬퍼 (EstateEditor와 동일한 정규식 처리)
  const n = (v) => {
    if (v === null || v === undefined || String(v).trim() === "") return null;
    const numVal = Number(String(v).replace(/[^0-9.-]/g, ""));
    return isNaN(numVal) ? null : numVal;
  };

  /**
   * 2. 📍 데이터 추출 (EstateEditor 로직 반영)
   * p가 상위 객체일 때(p.item)와 알맹이일 때(p)를 모두 대응합니다.
   * ?? (Nullish Coalescing)를 사용하여 0이나 ""은 통과시키고 오직 null/undefined만 체크합니다.
   */
  const bUp = n(p.bld_scale ?? p.item?.bld_scale);         // 지상 층수
  const bUnder = n(p.bld_scale_under ?? p.item?.bld_scale_under); // 지하 층수
  const floorTotal = n(p.floor_total ?? p.item?.floor_total);

  // 📍 [추가] 상세보기에서 처럼 변수 거치지 않고 직접 확인용
  const rawUnderValue = p.bld_scale_under ?? p.item?.bld_scale_under;

  let bScale = null;

  // 3. 출력 로직 (EstateEditor 스타일의 엄격한 체크)
  if (bUp !== null || bUnder !== null) {
    const upStr = bUp !== null ? `지상 ${bUp}` : "";
    const underStr = bUnder !== null ? `지하 ${bUnder}` : "";
    
    // 지상과 지하 정보가 둘 다 존재한다면 (내용이 0이라도)
    if (upStr !== "" && underStr !== "") {
      bScale = `${upStr} / ${underStr}`;
    } else {
      bScale = upStr || underStr;
    }
  } else if (floorTotal !== null) {
    bScale = `${floorTotal}층`;
  }

  // 이용역과 거리
  const station = p.station || null;
  const sd = n(p.station_distance);
  const stationLine = station
    ? `${station}${sd !== null ? ` / ${sd}분` : ""}`
    : sd !== null
    ? `${sd}분`
    : null;

  // 의뢰인 정보 1~5
// 📍 [데이터 파싱] JSON 문자열인 경우 객체로 변환
  const parseArr = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try { return JSON.parse(val); } catch (e) { return []; }
  };

  const owners = parseArr(p.owners);
  const tenants = parseArr(p.tenants);

  // 📍 [폴백 로직] 만약 새 컬럼이 비어있다면 기존 contact_name1 기반으로 하위 호환 유지
  const oldContacts = [];
  if (owners.length === 0 && tenants.length === 0) {
    for (let i = 1; i <= 5; i++) {
      const num = String(p[`contact_phone${i}`] ?? "").trim();
      if (num && num !== "0") {
        oldContacts.push({ name: p[`contact_name${i}`], number: num });
      }
    }
  }

  return (
    <div className="detail-contents-left" style={{ width: "100%", wordBreak: "break-all", whiteSpace: "normal" }}>
      <Field label="등록일" value={fmtDateTime(p.register_date, p.register_time)} />
      <Field label="사용승인일" value={p.completion_year || null} />
      <Field label="건물규모" value={bScale} />
      <div style={{ marginTop: 4 }}>
        <Field label="" value={p.register_memo || null} />
      </div>
      
      {/* 📍 [출력] 배열 기반으로 각각 출력 */}
      {owners.length > 0 && <ContactsBlock title="소유자" lines={owners} />}
      {tenants.length > 0 && <ContactsBlock title="임차인" lines={tenants} />}
      
      {/* 📍 [폴백 출력] 새 데이터가 아예 없을 때만 구형 데이터 출력 */}
      {owners.length === 0 && tenants.length === 0 && oldContacts.length > 0 && (
        <ContactsBlock title="연락처" lines={oldContacts} />
      )}
    </div>
  );
}

// [Right] ─── <관리비항목> / <거주인정보> / <난방방식>
function CommonDetailRightOnly({ p }) {
  // 관리비 포함내역
  const mgrItems = [
    yn(p.price_mgr_item_basic) ? "기본관리비" : null,
    yn(p.price_mgr_item_water) ? "수도" : null,
    yn(p.price_mgr_item_internet) ? "인터넷" : null,
    yn(p.price_mgr_item_online) ? "유선" : null,
    yn(p.price_mgr_item_heat) ? "난방" : null,
    yn(p.price_mgr_item_elec) ? "전기" : null,
  ].filter(Boolean);
  const mgrList = mgrItems.length ? mgrItems.join(" / ") : null;

  // 거주인정보
  const resident =
    p.resident_name || p.resident_phone || p.resident_memo
      ? [p.resident_name, p.resident_phone, p.resident_memo].filter(Boolean).join(" / ")
      : null;

  // 난방방식 매핑
  const heatMapRes = { "private-heat": "개별난방", center: "중앙난방", floor: "층난방" };
  const heatRes = heatMapRes[String(p.heat_condition || "").toLowerCase()] || null;

  // 특화 시설 정보 (상업/업무)
  const featuresCommerce = [
    yn(p.internal_condition_interior) && "인테리어",
    yn(p.internal_condition_water) && "수도시설",
    yn(p.internal_condition_city_gas) && "도시가스",
  ].filter(Boolean).join(" / ");

  const featuresBusiness = [
    yn(p.internal_condition_interior) && "인테리어",
    yn(p.bath_division) && "화장실 남녀구분",
    yn(p.bath_inside) && "화장실 내부",
    yn(p.external_condition_exsign) && "옥외광고",
  ].filter(Boolean).join(" / ");

  return (
    <div className="detail-contents-right" style={{ textAlign: "left", width: "100%" }}>
      <Field label="" value={mgrList} />
      <Field label="거주인" value={resident} />
      {p.estate_type === "residence" && heatRes && (
        <Field label="" value={heatRes} />
      )}
      {p.estate_type === "commerce" && featuresCommerce && (
        <Field value={featuresCommerce} />
      )}
      {p.estate_type === "business" && featuresBusiness && (
        <Field label="" value={featuresBusiness} />
      )}
    </div>
  );
}

function ResidenceDetail({ p }) {
  const n = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
  const exm2 = n(p.exclusive_scale);
  const expy = n(p.exclusive_scale_square);
  const room = n(p.room_cnt);
  const bath = n(p.bath_cnt);
  const floorDisp =
    p.estate_dong || p.estate_ho || p.floor_etc || p.floor_type1 || p.floor_type2
      ? [p.estate_dong ? `${p.estate_dong}동` : null, p.estate_ho ? `${p.estate_ho}호` : null]
          .filter(Boolean)
          .join(" / ")
      : null;
  const heatMap = { "private-heat":"개별난방", center:"중앙난방", floor:"층난방" };
  const heat = heatMap[String(p.heat_condition || "").toLowerCase()] || null;

  // commerce 시설
  const featuresCommerce = [
    yn(p.internal_condition_interior) ? "인테리어" : null,
    yn(p.internal_condition_water)    ? "수도시설" : null,
    yn(p.internal_condition_city_gas) ? "도시가스" : null,
  ].filter(Boolean).join(" / ") || null;

  // business 시설+냉난방
  const featuresBusiness = [
    yn(p.internal_condition_interior) ? "인테리어" : null,
    yn(p.bath_division)               ? "화장실 남녀구분" : null,
    yn(p.bath_inside)                 ? "화장실 내부" : null,
    yn(p.external_condition_exsign)   ? "옥외광고" : null,
  ].filter(Boolean).join(" / ") || null;
  const heatMapBiz = { center:"중앙난방", floor:"층별난방", "private-heat":"개별난방" };
  const coldMapBiz = { center:"중앙냉방", private:"개별냉방" };
  const hvac = [
    heatMapBiz[String(p.heat_condition||"").toLowerCase()] || null,
    coldMapBiz[String(p.cold_condition||"").toLowerCase()] || null,
  ].filter(Boolean).join(" / ") || null;


  return (
    <Section
      title="주거형"
      left={
        <>
          <Field label="베란다" value={String(p.living_condition_terrace || "").toLowerCase() === "y" ? "있음" : null} />
        </>
      }
      right={
        <>
          <Field label="난방방식" value={heat} />
        </>
      }
    />
  );
}

function ProfitDetail({ p }) {
  const n = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
  const oroom = n(p.owner_room);
  const obath = n(p.owner_bath);
  return null; // 다음 단계에서 실제 섹션 붙일 예정
}

function BusinessDetail({ p }) {
  const n = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
  const um2 = n(p.used_scale);
  const upy = n(p.used_scale_square);
  const features = [
    yn(p.internal_condition_interior) ? "인테리어" : null,
    yn(p.bath_division)               ? "화장실 남녀구분" : null,
    yn(p.bath_inside)                 ? "화장실 내부" : null,
    yn(p.external_condition_exsign)   ? "옥외광고" : null,
  ].filter(Boolean).join(" / ") || null;
  const heatMap = { center:"중앙난방", floor:"층별난방", "private-heat":"개별난방" };
  const coldMap = { center:"중앙냉방", private:"개별냉방" };
  const hvac = [heatMap[String(p.heat_condition||"").toLowerCase()]||null,
                coldMap[String(p.cold_condition||"").toLowerCase()]||null]
               .filter(Boolean).join(" / ") || null;
  return (
    <Section
      title="업무용"
      left={
        <>
          <Field label="실면적" value={um2 !== null || upy !== null ? `실 ${um2 ?? "-"}㎡ (${upy ?? "-"} 평)` : null} />
          <Field label="층" value={p.floor || null} />
          <Field label="명칭" value={p.shop_name || p.estate_name || null} />
        </>
      }
      right={
        <>
          <Field label="시설" value={features} />
          <Field label="냉난방" value={hvac} />
        </>
      }
    />
  );
}

function CommerceDetail({ p }) {
  const n = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
  const um2 = n(p.used_scale);
  const upy = n(p.used_scale_square);
  const features = [
    yn(p.internal_condition_interior)   ? "인테리어" : null,
    yn(p.internal_condition_water)      ? "수도시설" : null,
    yn(p.internal_condition_city_gas)   ? "도시가스" : null,
  ].filter(Boolean).join(" / ") || null;

  return (
    <Section
      title="상업용"
      left={
        <>
          <Field label="실면적" value={um2 !== null || upy !== null ? `실 ${um2 ?? "-"}㎡ (${upy ?? "-"} 평)` : null} />
          <Field label="층" value={p.floor || null} />
          <Field label="명칭" value={p.shop_name || p.estate_name || null} />
        </>
      }
      right={
        <>
          <Field label="시설" value={features} />
        </>
      }
    />
  );
}

